import { useState, type ReactNode } from 'react'
import type { Locale } from '@diana/shared'
import { api } from '../lib/api.js'
import { useLocale } from '../i18n/index.js'

/**
 * The email list is the only audience Diana actually owns — Instagram can
 * change its algorithm or ban an account overnight, this list can't.
 *
 * Nobody wants "a newsletter", so trade something for the address.
 * See docs/01-WEBSITE.md §4.
 */

type Strings = {
  done: string
  eyebrow: string
  heading: ReactNode
  body: string
  emailLabel: string
  placeholder: string
  sending: string
  submit: string
  error: string
}

const T: Record<Locale, Strings> = {
  pl: {
    done: 'Dziękuję — sprawdź skrzynkę.',
    eyebrow: 'Za darmo',
    heading: (
      <>
        50 polskich zwrotów, <em className="not-italic text-gold">których nie ma w podręcznikach</em>
      </>
    ),
    body: 'Wyślę Ci je na maila. Bez spamu — wypiszesz się jednym kliknięciem.',
    emailLabel: 'Adres e-mail',
    placeholder: 'twoj@email.com',
    sending: 'Wysyłam…',
    submit: 'Wyślij mi PDF',
    error: 'Nie udało się zapisać. Spróbuj jeszcze raz.',
  },
  uk: {
    done: 'Дякую — перевір пошту.',
    eyebrow: 'Безкоштовно',
    heading: (
      <>
        50 польських фраз, <em className="not-italic text-gold">яких немає в підручниках</em>
      </>
    ),
    body: 'Надішлю їх тобі на пошту. Без спаму — відпишешся одним кліком.',
    emailLabel: 'Адреса e-mail',
    placeholder: 'tviy@email.com',
    sending: 'Надсилаю…',
    submit: 'Надішли мені PDF',
    error: 'Не вдалося записатися. Спробуй ще раз.',
  },
  ru: {
    done: 'Спасибо — проверь почту.',
    eyebrow: 'Бесплатно',
    heading: (
      <>
        50 польских фраз, <em className="not-italic text-gold">которых нет в учебниках</em>
      </>
    ),
    body: 'Пришлю их тебе на почту. Без спама — отпишешься одним кликом.',
    emailLabel: 'Адрес e-mail',
    placeholder: 'tvoy@email.com',
    sending: 'Отправляю…',
    submit: 'Пришли мне PDF',
    error: 'Не получилось подписаться. Попробуй ещё раз.',
  },
}

export function NewsletterForm() {
  const locale = useLocale()
  const s = T[locale]
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setState('sending')
    try {
      await api.subscribe(email, locale)
      setState('done')
      setEmail('')
    } catch {
      setState('error')
    }
  }

  return (
    <div className="relative border border-gold/40 bg-porcelain px-8 py-12 text-center sm:px-14">
      {/* Inner hairline — the letterpress trick. */}
      <div aria-hidden className="pointer-events-none absolute inset-2 border border-gold/15" />

      <div className="relative">
        {state === 'done' ? (
          <p className="h-display text-2xl italic text-gold-600">{s.done}</p>
        ) : (
          <>
            <p className="eyebrow justify-center">{s.eyebrow}</p>
            {/* TODO(phase-1a): replace with the real lead magnet — docs/01-WEBSITE.md §4 */}
            <h2 className="h-display mx-auto mt-5 max-w-lg text-2xl leading-snug sm:text-3xl">
              {s.heading}
            </h2>
            <p className="mx-auto mt-5 max-w-md leading-relaxed font-light text-ink-soft">
              {s.body}
            </p>

            <form onSubmit={handleSubmit} className="mx-auto mt-9 flex max-w-lg flex-col gap-3 sm:flex-row">
              <label className="sr-only" htmlFor="newsletter-email">
                {s.emailLabel}
              </label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={s.placeholder}
                className="field flex-1"
              />
              <button type="submit" disabled={state === 'sending'} className="btn btn-gold disabled:opacity-60">
                {state === 'sending' ? s.sending : s.submit}
              </button>
            </form>

            {state === 'error' && <p className="mt-4 text-sm text-rose">{s.error}</p>}
          </>
        )}
      </div>
    </div>
  )
}
